import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Pricing } from '../../../../../types/cms.types';
import { VIETNAM_PROVINCES } from '../../../../../utils/provinces';
import './CpnCopyPricingDialog.css';

interface CpnCopyPricingDialogProps {
    visible: boolean;
    onHide: () => void;
    sourcePricing: Pricing | null;
    onCopy: (targetProvinces: string[]) => Promise<void>;
}

export const CpnCopyPricingDialog: React.FC<CpnCopyPricingDialogProps> = ({
    visible,
    onHide,
    sourcePricing,
    onCopy
}) => {
    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [selectedProvinces, setSelectedProvinces] = useState<string[]>([]);

    if (!sourcePricing) return null;

    // Tạo danh sách các tỉnh thành có thể chọn (loại bỏ tỉnh thành nguồn)
    const availableProvinces = VIETNAM_PROVINCES
        .filter(province => province !== sourcePricing.province)
        .map(province => ({
            label: province,
            value: province
        }));

    const handleCopy = async () => {
        if (selectedProvinces.length === 0) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Cảnh báo',
                detail: 'Vui lòng chọn ít nhất một tỉnh thành đích'
            });
            return;
        }

        try {
            setLoading(true);
            await onCopy(selectedProvinces);

            toast.current?.show({
                severity: 'success',
                summary: 'Thành công',
                detail: `Đã sao chép bảng giá cho ${selectedProvinces.length} tỉnh thành`
            });

            // Reset và đóng dialog
            setSelectedProvinces([]);
            onHide();
        } catch (error) {
            console.error('Error copying pricing:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Lỗi',
                detail: error instanceof Error ? error.message : 'Có lỗi xảy ra khi sao chép bảng giá'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedProvinces([]);
        onHide();
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                style={{ width: '600px' }}
                header="Sao chép bảng giá"
                modal
                className="p-fluid copy-pricing-dialog"
                onHide={handleCancel}
                footer={
                    <div className="p-dialog-footer">
                        <Button
                            label="Hủy"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={handleCancel}
                            disabled={loading}
                        />
                        <Button
                            label={`Sao chép cho ${selectedProvinces.length} tỉnh thành`}
                            icon="pi pi-copy"
                            loading={loading}
                            onClick={handleCopy}
                            disabled={selectedProvinces.length === 0}
                        />
                    </div>
                }
            >
                <div className="copy-pricing-content">
                    {/* Thông tin bảng giá nguồn */}
                    <div className="source-pricing-info">
                        <h3>Thông tin bảng giá nguồn</h3>
                        <div className="source-info-card">
                            <div className="info-row">
                                <span className="label">Tỉnh thành:</span>
                                <span className="value font-semibold">{sourcePricing.province}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Loại xe:</span>
                                <span className="value">
                                    {sourcePricing.vehicle_type === 'motorbike' ? 'Xe máy' :
                                        `Ô tô ${sourcePricing.car_seats} chỗ`}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="label">Giá cơ bản:</span>
                                <span className="value font-semibold text-primary">
                                    {new Intl.NumberFormat('vi-VN', {
                                        style: 'currency',
                                        currency: 'VND',
                                        minimumFractionDigits: 0
                                    }).format(sourcePricing.base_price)}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="label">Phí dịch vụ:</span>
                                <span className="value">
                                    {new Intl.NumberFormat('vi-VN', {
                                        style: 'currency',
                                        currency: 'VND',
                                        minimumFractionDigits: 0
                                    }).format(sourcePricing.service_fee)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Chọn tỉnh thành đích */}
                    <div className="target-provinces-section">
                        <h3>Chọn tỉnh thành đích</h3>
                        <div className="field">
                            <label htmlFor="targetProvinces">
                                Các tỉnh thành sẽ áp dụng bảng giá này *
                            </label>
                            <MultiSelect
                                id="targetProvinces"
                                value={selectedProvinces}
                                options={availableProvinces}
                                onChange={(e) => setSelectedProvinces(e.value)}
                                placeholder="Chọn các tỉnh thành"
                                filter
                                selectAll
                                maxSelectedLabels={3}
                                selectedItemsLabel="{0} tỉnh thành được chọn"
                                className="w-full"
                                display="chip"
                            />
                            <small className="text-muted">
                                Bảng giá sẽ được sao chép cho các tỉnh thành được chọn.
                                Nếu tỉnh thành đã có bảng giá thì sẽ được cập nhật.
                            </small>
                        </div>
                    </div>

                    {/* Thông tin bổ sung */}
                    {selectedProvinces.length > 0 && (
                        <div className="additional-info">
                            <div className="info-box">
                                <i className="pi pi-info-circle" />
                                <div>
                                    <strong>Lưu ý:</strong>
                                    <ul>
                                        <li>Tất cả thông tin giá từ <strong>{sourcePricing.province}</strong> sẽ được sao chép</li>
                                        <li>Nếu tỉnh thành đích đã có bảng giá, dữ liệu cũ sẽ được ghi đè</li>
                                        <li>Không thể hoàn tác sau khi thực hiện</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </>
    );
};
